import React, { useState, useEffect, useRef, useCallback } from "react";
import api from "@/lib/api";
import { NavLink, useNavigate, Outlet, Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import NotificationsBell from "@/components/NotificationsBell";
import {
  CalendarBlank, Truck, HardHat, Buildings, UsersThree, Storefront,
  Sliders, SignOut, Gear, Path, ShieldCheck, Question, MagnifyingGlass,
  CaretDoubleLeft, CaretDoubleRight, X,
} from "@phosphor-icons/react";

const NAV_GROUPS = [
  { label: "Operations", items: [
    { to: "/", label: "Scheduler", icon: CalendarBlank, end: true, testid: "nav-scheduler", countKey: "scheduler", countTitle: "Dockets awaiting approval" },
    { to: "/projects", label: "Projects", icon: Buildings, testid: "nav-projects" },
    { to: "/transport", label: "Transport", icon: Path, testid: "nav-transport" },
  ]},
  { label: "Registers", items: [
    { to: "/assets", label: "Assets", icon: Truck, testid: "nav-assets" },
    { to: "/operators", label: "Workers", icon: HardHat, testid: "nav-operators", countKey: "operators", countTitle: "Timesheets awaiting approval" },
    { to: "/customers", label: "Customers", icon: Storefront, testid: "nav-customers" },
    { to: "/suppliers", label: "Suppliers", icon: UsersThree, testid: "nav-suppliers" },
  ]},
  { label: "Compliance", items: [
    { to: "/compliance", label: "Compliance", icon: ShieldCheck, testid: "nav-compliance", countKey: "compliance", countTitle: "Expired compliance items" },
  ]},
  { label: "System", items: [
    { to: "/data-management", label: "Data Management", icon: Sliders, testid: "nav-data-management" },
  ]},
];

/* -------------------------- Global jump-to search ------------------------- */
const KIND_TAG = {
  project: { l: "PROJECT", c: "bg-[#2B2B2B] text-[#B8B4AC]" },
  asset: { l: "PLANT", c: "bg-[#2B2B2B] text-[#B8B4AC]" },
  worker: { l: "WORKER", c: "bg-[#2B2B2B] text-[#B8B4AC]" },
};

function GlobalSearch() {
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const ref = useRef(null);
  const inputRef = useRef(null);

  const loadData = useCallback(async () => {
    if (data) return;
    try {
      const [p, a, o] = await Promise.all([api.get("/projects"), api.get("/assets"), api.get("/operators")]);
      setData({ projects: p.data || [], assets: a.data || [], operators: o.data || [] });
    } catch { setData({ projects: [], assets: [], operators: [] }); }
  }, [data]);

  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault(); inputRef.current?.focus(); setOpen(true); loadData();
      }
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [loadData]);

  useEffect(() => {
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const t = q.trim().toLowerCase();
  const results = [];
  if (t && data) {
    (data.projects || []).forEach((p) => {
      const hay = `${p.project_name || ""} ${p.site_address || ""} ${p.client || ""} ${p.project_no || ""}`.toLowerCase();
      if (hay.includes(t)) results.push({ kind: "project", id: p.id, primary: p.project_name || p.site_address, secondary: p.client || p.site_address, to: "/projects" });
    });
    (data.assets || []).forEach((a) => {
      const hay = `${a.asset_no || ""} ${a.asset_type || ""} ${a.supplier || ""}`.toLowerCase();
      if (hay.includes(t)) results.push({ kind: "asset", id: a.id, primary: a.asset_no, secondary: a.asset_type || a.supplier || "", to: "/assets" });
    });
    (data.operators || []).forEach((o) => {
      const hay = `${o.name || ""} ${(o.skills || []).join(" ")}`.toLowerCase();
      if (hay.includes(t)) results.push({ kind: "worker", id: o.id, primary: o.name, secondary: (o.skills || []).slice(0, 2).join(", ") || "Worker", to: "/operators" });
    });
  }
  const shown = results.slice(0, 8);

  const go = (r) => { setOpen(false); setQ(""); navigate(r.to); };

  return (
    <div className="relative hidden md:block" ref={ref} data-testid="global-search">
      <MagnifyingGlass size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8A8A8A] pointer-events-none" />
      <input
        ref={inputRef}
        value={q}
        onChange={(e) => { setQ(e.target.value); setOpen(true); }}
        onFocus={() => { setOpen(true); loadData(); }}
        data-testid="global-search-input"
        placeholder="Jump to project, asset or worker"
        className="w-[300px] lg:w-[360px] h-9 pl-9 pr-14 bg-[#1C1C1C] border border-[#333333] text-white placeholder-[#8A8A8A] text-[13px] rounded-[9px] focus:border-white/40 focus:ring-2 focus:ring-equipr-yellow/30 focus:outline-none"
      />
      <span className="absolute right-2.5 top-1/2 -translate-y-1/2 font-mono text-[10px] text-[#8A8A8A] border border-[#333333] rounded px-1.5 py-0.5 pointer-events-none">⌘K</span>
      {open && t && (
        <div className="absolute left-0 top-11 z-50 w-[380px] bg-surface-panel border border-border-warm rounded-[12px] overflow-hidden shadow-modal" data-testid="global-search-results">
          {!data ? (
            <div className="px-4 py-6 text-center text-[13px] text-ink-tertiary">Searching…</div>
          ) : shown.length === 0 ? (
            <div className="px-4 py-6 text-center text-[13px] text-ink-tertiary" data-testid="global-search-empty">No matches for “{q.trim()}”.</div>
          ) : shown.map((r) => (
            <button key={`${r.kind}-${r.id}`} onClick={() => go(r)} data-testid={`global-search-result-${r.id}`}
              className="w-full flex items-center gap-2.5 px-3 py-2.5 border-b border-divider-row last:border-0 hover:bg-accent-wash text-left">
              <span className={`shrink-0 font-mono text-[9px] font-bold tracking-wide px-1.5 py-0.5 rounded ${KIND_TAG[r.kind].c}`}>{KIND_TAG[r.kind].l}</span>
              <span className="min-w-0 flex-1">
                <span className="block text-[13px] font-semibold text-ink-primary truncate">{r.primary}</span>
                <span className="block text-[11.5px] text-ink-secondary truncate">{r.secondary}</span>
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/* -------------------------------- Nav item -------------------------------- */
function NavItem({ n, count, collapsed }) {
  return (
    <NavLink to={n.to} end={n.end} data-testid={n.testid} title={collapsed ? n.label : undefined}
      className={({ isActive }) =>
        `group flex items-center rounded-[8px] transition-colors ${collapsed ? "justify-center mx-2 h-10 w-10" : "mx-3 gap-2.5 px-3 py-2"} ${
          isActive ? "bg-equipr-yellow text-black font-bold" : "font-medium text-[#B8B4AC] hover:bg-[#1E1E1E] hover:text-white"
        }`}>
      {({ isActive }) => (
        <>
          <n.icon size={collapsed ? 20 : 18} weight="bold" className="shrink-0" />
          {!collapsed && <span className="flex-1 text-[12px] uppercase tracking-[0.05em] truncate">{n.label}</span>}
          {count > 0 && (
            <span title={n.countTitle} data-testid={`nav-count-${n.countKey}`}
              className={collapsed
                ? `absolute translate-x-3 -translate-y-3 w-2 h-2 rounded-full ${n.countKey === "compliance" ? "bg-[#FF7A3D]" : "bg-equipr-yellow"}`
                : `font-mono text-[10px] font-bold px-1.5 rounded-[4px] leading-4 border ${n.countKey === "compliance" ? "bg-[#3A1405] text-[#FF7A3D] border-[#5A2408]" : (isActive ? "bg-black/15 text-black border-transparent" : "bg-[#3A3006] text-equipr-yellow border-[#574800]")}`}>
              {count}
            </span>
          )}
        </>
      )}
    </NavLink>
  );
}

export default function AppShell() {
  const { user, logout, isAdmin } = useAuth();
  const navigate = useNavigate();
  const [counts, setCounts] = useState({});
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem("eq_nav_collapsed") === "1");

  useEffect(() => {
    if (!user || user.role === "super_admin") return;
    const load = () => api.get("/nav/counts").then(({ data }) => setCounts(data)).catch(() => {});
    load();
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, [user]);

  if (user && user.role === "super_admin") return <Navigate to="/tenants" replace />;

  const scope = user?.role === "admin" ? "both" : (user?.resource_scope || "both");
  const groups = NAV_GROUPS.map((g) => ({
    ...g,
    items: g.items.filter((n) => {
      if (n.to === "/assets" && scope === "workers") return false;
      if ((n.to === "/operators" || n.to === "/compliance") && scope === "assets") return false;
      return true;
    }),
  })).filter((g) => g.items.length);

  const doLogout = async () => { await logout(); navigate("/login"); };
  const toggleCollapse = () => setCollapsed((c) => { localStorage.setItem("eq_nav_collapsed", c ? "0" : "1"); return !c; });
  const initial = (user?.name || "?").trim().charAt(0).toUpperCase();
  const tenantName = user?.tenant_name || "EQUIPR Civil";

  return (
    <div className="h-screen w-full flex flex-col overflow-hidden bg-surface-page font-sans">
      {/* Top bar */}
      <header className="h-16 bg-[#121212] flex items-center justify-between pl-4 pr-4 gap-4 shrink-0 z-30 border-b border-[#2B2B2B]">
        <div className="flex items-center gap-5">
          <div className="flex items-center gap-2 select-none" data-testid="brand-logo">
            <span className="font-extrabold text-lg tracking-tight text-white">EQUIPR</span>
            <span className="h-4 w-px bg-[#333333]" />
            <span className="text-[11px] font-semibold tracking-[0.22em] uppercase text-[#8A8A8A]">Civil</span>
          </div>
          <GlobalSearch />
        </div>
        <div className="flex items-center gap-1">
          <button data-testid="help-button" title="Help" className="w-9 h-9 flex items-center justify-center rounded-[8px] text-[#B8B4AC] hover:bg-[#1E1E1E] hover:text-white">
            <Question size={19} weight="bold" />
          </button>
          <div className="w-9 h-9 flex items-center justify-center rounded-[8px] text-[#B8B4AC] hover:bg-[#1E1E1E] hover:text-white">
            <NotificationsBell />
          </div>
          {isAdmin && (
            <NavLink to="/settings" data-testid="nav-settings" title="Settings"
              className={({ isActive }) => `w-9 h-9 flex items-center justify-center rounded-[8px] ${isActive ? "text-equipr-yellow bg-[#1E1E1E]" : "text-[#B8B4AC] hover:bg-[#1E1E1E] hover:text-white"}`}>
              <Gear size={19} weight="bold" />
            </NavLink>
          )}
          <div className="flex items-center gap-2.5 pl-3 ml-1 border-l border-[#2B2B2B]">
            <div className="w-8 h-8 rounded-[8px] bg-equipr-yellow text-black font-extrabold text-sm flex items-center justify-center shrink-0" data-testid="user-avatar">{initial}</div>
            <div className="hidden sm:block leading-tight">
              <div className="text-[13px] font-semibold text-white" data-testid="current-user-name">{user?.name}</div>
              <div className="text-[10px] uppercase tracking-[0.08em] text-[#8A8A8A]">{user?.role} · {tenantName}</div>
            </div>
            <button onClick={doLogout} data-testid="logout-button" title="Log out"
              className="ml-2 flex items-center gap-1.5 h-8 px-3 rounded-[7px] border border-[#333333] text-[#B8B4AC] text-[11px] font-bold uppercase tracking-[0.05em] hover:bg-[#1E1E1E] hover:text-white">
              <SignOut size={15} weight="bold" /> <span className="hidden lg:inline">Log out</span>
            </button>
          </div>
        </div>
      </header>

      <div className="flex-1 flex overflow-hidden">
        {/* Sidebar */}
        <aside className={`${collapsed ? "w-16" : "w-[260px]"} bg-[#121212] border-r border-[#2B2B2B] flex flex-col shrink-0 transition-[width] duration-200`} data-testid="app-sidebar">
          <nav className="flex-1 overflow-y-auto py-4">
            {groups.map((g) => (
              <div key={g.label} className="mb-4" data-testid={`nav-group-${g.label.toLowerCase()}`}>
                {collapsed ? <div className="mx-3 mb-1 border-t border-[#2B2B2B]" /> : (
                  <div className="px-[18px] pb-[7px] text-[9.5px] font-bold uppercase tracking-[0.16em] text-[#6E6E6E]">{g.label}</div>
                )}
                <div className="space-y-0.5">
                  {g.items.map((n) => (
                    <div key={n.to} className="relative">
                      <NavItem n={n} count={n.countKey ? (counts[n.countKey] || 0) : 0} collapsed={collapsed} />
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </nav>
          <button onClick={toggleCollapse} data-testid="collapse-menu-toggle"
            className={`flex items-center gap-2 h-11 border-t border-[#2B2B2B] text-[#6E6E6E] hover:text-white hover:bg-[#1E1E1E] ${collapsed ? "justify-center" : "px-[18px]"}`}>
            {collapsed ? <CaretDoubleRight size={16} weight="bold" /> : <><CaretDoubleLeft size={16} weight="bold" /><span className="text-[9.5px] font-bold uppercase tracking-[0.16em]">Collapse menu</span></>}
          </button>
        </aside>

        <main className="flex-1 overflow-hidden">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
